/**
 * The five live push checks — V7-01 §1.
 *
 * "Every failure in the enable flow surfaces as a visible message, never a
 * silent no-op — the V5-05 discipline applied to the client."
 *
 * The founder's report was *no notification arrives, and the site is not even
 * in Firefox's permission list*, which is four questions wearing one coat: is
 * this a secure context, is there a service worker, was permission ever asked
 * for, is this browser registered, and does a real send reach it. Answering
 * them one at a time on screen turns "push is broken" into a sentence naming
 * which link failed and what to do about it.
 *
 * Reading the browser is separated from judging what it says: `readEnvironment`
 * touches globals, `evaluateChecks` is pure. That is what lets the unit tests
 * assert the remedies — including the macOS System Settings layer, which is
 * the one nobody thinks of and the one that silently swallows every
 * notification on a Mac.
 */

export type CheckStatus = 'pass' | 'fail' | 'warn';

export interface DiagnosticCheck {
  id:
    | 'https'
    | 'service-worker'
    | 'permission'
    | 'subscription'
    | 'test-send'
    // V8-01 §1.5 — what the worker did with the last push it actually received.
    // Distinct from `test-send`, which is what the *server* did with one.
    | 'worker-display'
    /**
     * V9-03 §2 — the loopback's three observable hops.
     *
     * Separate ids because the entire point is that a person can say *which*
     * one is red. "Delivery failed" is what has been reported for three
     * versions and it is not actionable by anybody.
     */
    | 'hop-recorded'
    | 'hop-accepted'
    | 'hop-displayed';
  label: string;
  status: CheckStatus;
  /** What is true right now, in one short phrase. */
  detail: string;
  /** What to do about it. Empty when there is nothing to do. */
  remedy?: string;
}

export interface PushEnvironment {
  secureContext: boolean;
  supported: boolean;
  serviceWorkerRegistered: boolean;
  /** The built SW's cache-busting hash, so "which version" has an answer. */
  serviceWorkerVersion: string | null;
  permission: NotificationPermission | 'unsupported';
  hasLocalSubscription: boolean;
  /** Devices the *server* knows about for this person. */
  registeredDevices: number;
  platformConfigured: boolean;
  /** Best-effort browser identification, for the per-browser remedy text. */
  browser: 'firefox' | 'chrome' | 'edge' | 'safari' | 'other';
  isMac: boolean;
}

/**
 * Where a blocked site is un-blocked, per browser.
 *
 * Written out rather than linked because the settings path is three levels
 * deep in every one of them and "check your browser settings" is the sentence
 * that ends a support conversation without ending the problem.
 */
export function unblockInstructions(
  browser: PushEnvironment['browser'],
  isMac: boolean,
): string {
  const site =
    browser === 'firefox'
      ? 'Firefox: click the padlock in the address bar → Connection secure → More information → Permissions → clear “Send Notifications”. Or Settings → Privacy & Security → Notifications → Settings, and remove this site.'
      : browser === 'chrome'
        ? 'Chrome: click the icon left of the address → Site settings → Notifications → Allow.'
        : browser === 'edge'
          ? 'Edge: click the padlock → Permissions for this site → Notifications → Allow.'
          : browser === 'safari'
            ? 'Safari: Settings → Websites → Notifications → find this site → Allow. On iPhone and iPad you must first add VirtueTasks to the Home Screen.'
            : 'Open your browser’s site settings for this page and allow notifications.';

  // The layer people miss. A Mac can grant a site permission in the browser and
  // still show nothing, because the *browser itself* is muted at the OS level —
  // and Focus modes do it silently.
  const mac = isMac
    ? ' Then check macOS: System Settings → Notifications → select your browser → Allow notifications on, and make sure Do Not Disturb / a Focus mode is not on.'
    : '';

  return `${site}${mac}`;
}

/** The five rows, given a reading of the environment. Pure. */
export function evaluateChecks(env: PushEnvironment): DiagnosticCheck[] {
  const checks: DiagnosticCheck[] = [];

  checks.push({
    id: 'https',
    label: 'Secure connection (HTTPS)',
    status: env.secureContext ? 'pass' : 'fail',
    detail: env.secureContext
      ? 'This page is served over a secure connection.'
      : 'This page is not a secure context.',
    remedy: env.secureContext
      ? undefined
      : 'Push requires HTTPS. Open VirtueTasks on its https:// address — localhost is the only exception browsers make.',
  });

  checks.push({
    id: 'service-worker',
    label: 'Service worker registered',
    status: !env.supported ? 'fail' : env.serviceWorkerRegistered ? 'pass' : 'fail',
    detail: !env.supported
      ? 'This browser does not support service workers.'
      : env.serviceWorkerRegistered
        ? `Active${env.serviceWorkerVersion ? ` (${env.serviceWorkerVersion})` : ''}.`
        : 'No service worker is controlling this page.',
    remedy: !env.supported
      ? 'Use Firefox, Chrome, Edge, or Safari 16.4+. Private windows can never subscribe.'
      : env.serviceWorkerRegistered
        ? undefined
        : 'Reload the page. If it stays unregistered, the browser may be in a private window, or an extension may be blocking service workers.',
  });

  const permission = env.permission;
  checks.push({
    id: 'permission',
    label: 'Browser permission',
    status:
      permission === 'granted' ? 'pass' : permission === 'denied' ? 'fail' : 'warn',
    detail:
      permission === 'granted'
        ? 'Granted for this site.'
        : permission === 'denied'
          ? 'Blocked for this site.'
          : permission === 'unsupported'
            ? 'This browser has no Notification API.'
            : 'Not asked yet — the browser has never shown a prompt for this site.',
    remedy:
      permission === 'granted'
        ? undefined
        : permission === 'denied'
          ? unblockInstructions(env.browser, env.isMac)
          : permission === 'unsupported'
            ? 'Use a browser that supports web notifications.'
            : 'Press “Enable on this device” above — that click is what allows the browser to show its prompt.',
  });

  checks.push({
    id: 'subscription',
    label: 'This device is registered',
    status: env.hasLocalSubscription && env.registeredDevices > 0 ? 'pass' : 'fail',
    detail:
      env.hasLocalSubscription && env.registeredDevices > 0
        ? `Registered — ${env.registeredDevices} device${env.registeredDevices === 1 ? '' : 's'} on your account.`
        : env.hasLocalSubscription
          ? 'This browser has a subscription, but the server has no record of it.'
          : env.registeredDevices > 0
            ? 'Other devices are registered, but this browser is not.'
            : 'No devices registered.',
    remedy:
      env.hasLocalSubscription && env.registeredDevices > 0
        ? undefined
        : 'Press “Enable on this device” above.',
  });

  checks.push({
    id: 'test-send',
    label: 'Delivery',
    status: env.platformConfigured ? 'warn' : 'fail',
    detail: env.platformConfigured
      ? 'Not tested yet.'
      : 'Push is not configured on this installation.',
    remedy: env.platformConfigured
      ? 'Press “Send a test to this device”.'
      : 'A platform administrator has to generate the VAPID keys in the platform console.',
  });

  return checks;
}

/** Reads the browser. The only impure function here. */
export async function readEnvironment(input: {
  registeredDevices: number;
  platformConfigured: boolean;
}): Promise<PushEnvironment> {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  let serviceWorkerRegistered = false;
  let serviceWorkerVersion: string | null = null;
  let hasLocalSubscription = false;

  if (supported) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      serviceWorkerRegistered = Boolean(registration?.active);
      // The script URL carries the build's cache-busting query or hash, which
      // is the only version identifier a generated worker has.
      serviceWorkerVersion = registration?.active?.scriptURL
        ? new URL(registration.active.scriptURL).pathname.split('/').pop() ?? null
        : null;
      hasLocalSubscription = Boolean(await registration?.pushManager.getSubscription());
    } catch {
      serviceWorkerRegistered = false;
    }
  }

  return {
    secureContext: typeof window !== 'undefined' && window.isSecureContext,
    supported,
    serviceWorkerRegistered,
    serviceWorkerVersion,
    permission: supported ? Notification.permission : 'unsupported',
    hasLocalSubscription,
    registeredDevices: input.registeredDevices,
    platformConfigured: input.platformConfigured,
    browser: /Firefox\//.test(ua)
      ? 'firefox'
      : /Edg\//.test(ua)
        ? 'edge'
        : /Chrome\//.test(ua)
          ? 'chrome'
          : /Safari\//.test(ua)
            ? 'safari'
            : 'other',
    isMac: /Mac OS X|Macintosh/.test(ua),
  };
}
